import { Injectable } from '@angular/core';
import { ACTIVE_SUBSCRIPTIONS_CCV } from 'src/app/shared/common/constants';
import { bffErrorCode } from '../../core/api/bff-error';
import { GlobalStorage } from '../../core/services/globals.service';
import { IonicStorageService } from '../../core/services/storage/ionic/storage.service';
import { SUBSCRIPTION_STORAGE_KEYS } from '../constants/subscription-storage.constant';
import { toSubscriptionRequest } from '../mappers/subscription-request.mapper';
import {
  IEmployerRawFromService,
  ISubscriptionRequest,
  ISubscriptionResult,
} from '../models/subscription-api.model';
import { SubscriptionApiService } from './subscription-api.service';
import { SubscriptionStore } from './subscription.store';

@Injectable({ providedIn: 'root' })
export class SubscriptionService {
  private employersRequest?: Promise<IEmployerRawFromService[]>;

  constructor(
    private readonly api: SubscriptionApiService,
    private readonly store: SubscriptionStore,
    private readonly globalSvc: GlobalStorage,
    private readonly ionicStorage: IonicStorageService
  ) {}

  loadEmployers(): Promise<IEmployerRawFromService[]> {
    this.employersRequest ??= this.fetchEmployers();

    return this.employersRequest;
  }

  async prepareEntry(): Promise<void> {
    const employers = await this.loadEmployers();
    this.store.setHasEmployer(employers.length > 0);
  }

  async submit(executiveRut: string): Promise<void> {
    const draft = this.store.draft();

    if (!draft) throw new Error('Subscription draft is incomplete');

    const request = toSubscriptionRequest(
      draft,
      this.globalSvc.userData,
      executiveRut
    );
    sessionStorage.setItem(
      SUBSCRIPTION_STORAGE_KEYS.pending,
      JSON.stringify(request)
    );

    await this.send(request);
    this.store.reset();
  }

  async retry(): Promise<void> {
    const request = this.pendingRequest();

    if (!request) throw new Error('There is no pending subscription to retry');

    await this.send(request);
    sessionStorage.removeItem(SUBSCRIPTION_STORAGE_KEYS.errorCode);
  }

  private async send(request: ISubscriptionRequest): Promise<void> {
    let result: ISubscriptionResult;

    try {
      result = await this.api.submit(request);
    } catch (error) {
      sessionStorage.setItem(
        SUBSCRIPTION_STORAGE_KEYS.errorCode,
        bffErrorCode(error)
      );
      throw error;
    }

    sessionStorage.setItem(
      SUBSCRIPTION_STORAGE_KEYS.result,
      JSON.stringify({ ...request, ...result })
    );
    sessionStorage.removeItem(SUBSCRIPTION_STORAGE_KEYS.pending);
    sessionStorage.setItem(SUBSCRIPTION_STORAGE_KEYS.apvStatus, 'false');
    await this.ionicStorage.remove(ACTIVE_SUBSCRIPTIONS_CCV);
  }

  private pendingRequest(): ISubscriptionRequest | null {
    try {
      const raw = sessionStorage.getItem(SUBSCRIPTION_STORAGE_KEYS.pending);

      return raw ? (JSON.parse(raw) as ISubscriptionRequest) : null;
    } catch {
      return null;
    }
  }

  private async fetchEmployers(): Promise<IEmployerRawFromService[]> {
    try {
      return await this.api.getEmployers();
    } catch (error) {
      this.employersRequest = undefined;
      throw error;
    }
  }
}
